/**
 * Reusable internal-link block for service pages. Renders:
 *   1) A text-link matrix of "{Service} in {City}, TX" entries pointing
 *      to dedicated city project hubs. Cities with at least
 *      one matching case study are listed first and badged "Recent build".
 *   2) Cards for each real case study in this service category linking to its
 *      detail page (/case-studies/$slug).
 *   3) A footer link to the full filtered category index.
 *
 * Drop-in:
 *   <ServiceInternalLinks
 *     servicePath="/awnings"
 *     serviceLabel="Custom Awnings"
 *   />
 */

import { Link } from "@tanstack/react-router";
import { ArrowRight, MapPin } from "lucide-react";
import {
  SERVICE_PATH_TO_CATEGORY,
  getServiceInternalLinks,
  type ServiceCategory,
} from "@/lib/serviceLinks";

export interface ServiceInternalLinksProps {
  /** Service path, e.g. "/awnings". Used to look up the matching category. */
  servicePath: string;
  /** Display label, e.g. "Custom Awnings". Defaults to the category. */
  serviceLabel?: string;
  /** Optional override for the category — useful when the route has no entry
   *  in SERVICE_PATH_TO_CATEGORY but you still want a manual mapping. */
  category?: ServiceCategory;
  /** Limit number of case-study cards. Default 6. */
  maxStudies?: number;
  tone?: "cream" | "white";
  className?: string;
}

export function ServiceInternalLinks({
  servicePath,
  serviceLabel,
  category,
  maxStudies = 6,
  tone = "cream",
  className = "",
}: ServiceInternalLinksProps) {
  const resolvedCategory =
    category ?? SERVICE_PATH_TO_CATEGORY[servicePath];
  if (!resolvedCategory) return null;

  const label = serviceLabel ?? resolvedCategory;
  const { studies, cityLinks } = getServiceInternalLinks(
    resolvedCategory,
    label,
  );

  if (studies.length === 0 && cityLinks.length === 0) return null;

  const visibleStudies = studies.slice(0, maxStudies);
  const surfaceClass =
    tone === "white" ? "bg-white text-navy-deep" : "bg-cream text-navy-deep";

  return (
    <section
      className={`border-t-8 border-navy-deep py-16 lg:py-20 ${surfaceClass} ${className}`}
      aria-labelledby={`service-internal-links-${resolvedCategory
        .toLowerCase()
        .replace(/\s+/g, "-")}`}
    >
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="mb-10 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
              ★ {label} Across Texas
            </div>
            <h2
              id={`service-internal-links-${resolvedCategory
                .toLowerCase()
                .replace(/\s+/g, "-")}`}
              className="mt-2 font-display text-3xl font-bold uppercase leading-tight md:text-4xl"
            >
              {label} by City — Texas Service Areas
            </h2>
            <p className="mt-3 text-base text-navy-deep/75">
              Browse real {label.toLowerCase()} case studies, or jump to the
               project hub for your city. Open the
               {` `}
              <Link to="/case-studies" className="underline hover:text-red">
                case studies index
              </Link>{" "}
               to browse every documented build.
            </p>
          </div>
          <Link
            to="/case-studies"
            className="inline-flex items-center gap-2 self-start border-2 border-navy-deep px-4 py-2 font-display text-xs uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-white"
          >
            All {label} projects <ArrowRight className="h-3 w-3" />
          </Link>
        </div>

        {/* City × service text-link crawl matrix */}
        {cityLinks.length > 0 && (
          <nav
            aria-label={`${label} by city`}
            className="mb-12 border-y border-navy-deep/15 bg-white/40 py-6"
          >
            <ul className="grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
              {cityLinks.map((link) => (
                <li key={link.city.slug}>
                  <Link
                     to="/case-studies/in/$city"
                     params={{ city: link.city.slug }}
                    className="group flex items-center justify-between gap-3 py-1.5 text-sm text-navy-deep transition hover:text-red"
                  >
                    <span className="flex items-center gap-2">
                      <MapPin
                        className={`h-3.5 w-3.5 ${
                          link.hasMatch ? "text-red" : "text-navy-deep/70"
                        }`}
                      />
                      <span className="font-display uppercase tracking-wider">
                        {link.anchor}
                      </span>
                    </span>
                    {link.hasMatch && (
                      <span className="shrink-0 bg-red/10 px-2 py-0.5 font-display text-[10px] uppercase tracking-[0.2em] text-red">
                        Recent build
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        )}

        {/* Real case-study cards */}
        {visibleStudies.length > 0 && (
          <>
            <h3 className="font-display text-xs uppercase tracking-[0.3em] text-navy-deep/70">
              Featured {label.toLowerCase()} case studies
            </h3>
            <ul className="mt-4 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {visibleStudies.map((study) => (
                <li key={study.slug}>
                  <Link
                    to="/case-studies/$slug"
                    params={{ slug: study.slug }}
                    className="group flex h-full flex-col overflow-hidden border-2 border-navy-deep/15 bg-white transition hover:border-red"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden bg-navy-deep">
                      <img
                        src={study.images[0]?.src}
                        alt={study.images[0]?.alt ?? study.title}
                        width={800}
                        height={600}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                      />
                      <span className="absolute left-3 top-3 bg-red px-2 py-0.5 font-display text-[10px] uppercase tracking-[0.2em] text-white">
                        {study.category}
                      </span>
                    </div>
                    <div className="flex flex-1 flex-col gap-2 p-4">
                      <div className="flex items-center gap-1.5 font-display text-[10px] uppercase tracking-[0.25em] text-navy-deep/60">
                        <MapPin className="h-3 w-3 text-red" />{" "}
                        {study.location}
                      </div>
                      <h4 className="font-display text-base font-bold leading-tight text-navy-deep group-hover:text-red">
                        {study.title}
                      </h4>
                      <p className="text-sm text-navy-deep/70 line-clamp-2">
                        {study.summary}
                      </p>
                      <span className="mt-auto inline-flex items-center gap-1 font-display text-[11px] uppercase tracking-[0.2em] text-red">
                        Read case study <ArrowRight className="h-3 w-3" />
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
            {studies.length > visibleStudies.length && (
              <div className="mt-6 text-center">
                <Link
                  to="/case-studies"
                  search={{ category: resolvedCategory }}
                  className="inline-flex items-center gap-1 font-display text-xs uppercase tracking-[0.25em] text-red hover:text-red-deep"
                >
                  See all {studies.length} {label.toLowerCase()} projects{" "}
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
